import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  useToast,
  Text,
} from '@chakra-ui/react'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import type {
  LocationProfileContactFormValues,
  PrescriberLocationProfile,
} from '../../../types/location'
import { useUpdatePrescriberLocationProfileMutation } from '../prescriberLocationProfilesSlice'
import { LocationProfileContactFields } from './LocationProfileContactFields'

interface EditLocationProfileModalProps {
  isOpen: boolean
  onClose: () => void
  profile: PrescriberLocationProfile
  locationName: string
}

export const EditLocationProfileModal = ({
  isOpen,
  onClose,
  profile,
  locationName,
}: EditLocationProfileModalProps) => {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<LocationProfileContactFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      providerNumber: '',
      phoneNumber: '',
      email: '',
    },
  })

  const [updateProfile, { isLoading }] = useUpdatePrescriberLocationProfileMutation()
  const toast = useToast()

  useEffect(() => {
    if (isOpen) {
      reset({
        providerNumber: profile.providerNumber ?? '',
        phoneNumber: profile.phoneNumber || '',
        email: profile.email || '',
      })
    }
  }, [isOpen, profile, reset])

  const handleSave = async (data: LocationProfileContactFormValues) => {
    try {
      const payload: Partial<LocationProfileContactFormValues> = {}
      const providerNumber = data.providerNumber?.trim()
      if (providerNumber) {
        payload.providerNumber = providerNumber
      }
      const phoneNumber = data.phoneNumber?.trim()
      if (phoneNumber) {
        payload.phoneNumber = phoneNumber
      }
      const email = data.email?.trim()
      if (email) {
        payload.email = email
      }

      await updateProfile({
        locationProfileId: profile.id,
        data: payload,
      }).unwrap()

      toast({
        title: 'Successfully updated profile',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      })
      onClose()
    } catch (error: any) {
      toast({
        title: 'Failed to update profile',
        description: error?.data?.message || 'Please try again later',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      })
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay backdropFilter="blur(2px)" />
      <ModalContent borderRadius="xl">
        <ModalHeader fontSize="lg" fontWeight="bold" color="gray.900" pb={2}>
          Edit location profile
          <Text fontSize="sm" color="blue.500" fontWeight="normal">
            {locationName}
          </Text>
        </ModalHeader>
        <ModalCloseButton top={3} right={3} />

        <ModalBody py={4}>
          <VStack spacing={4} align="stretch">
            <LocationProfileContactFields
              register={register}
              setValue={setValue}
              watch={watch}
              errors={errors}
            />
          </VStack>
        </ModalBody>

        <ModalFooter pt={2} pb={6}>
          <Button variant="outline" mr={3} onClick={onClose} isDisabled={isLoading} size="md">
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit(handleSave)}
            isLoading={isLoading}
            size="md"
            px={8}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
